import React, { useRef, useState } from 'react'
import Select from 'react-select'
import { FieldOption } from "../../../types/ChampsObligatoires"
import { REQUIRED_FIELD_STATES, RequiredFieldStateCode } from "../../../enums/EtatChampObligatoire"
import { TYPES_CHAMP } from "../../../enums/TypeChamp"

interface SousBlocOption {
    id: number
    label: string
}

interface SelectOption {
    value: string
    label: string
}

interface TemplateFieldPayload {
    fieldId: number
    subBlockId: number | null
    type: string | null
    isCore: boolean
    defaultState: RequiredFieldStateCode
}

interface AjoutChampFormProps {
    fields: FieldOption[]
    sousBlocsExistants: SousBlocOption[]
    onCreateField: (label: string, code: string) => Promise<FieldOption>
    onCreateSubBloc: (label: string) => Promise<{ id: number }>
    onSubmit: (payloads: TemplateFieldPayload[]) => Promise<void>
    onCancel: () => void
}

const NOUVEAU_FIELD = 'nouveau'
const NOUVEAU_SOUS_BLOC = 'nouveau'
const AUCUN_SOUS_BLOC = 'aucun'

const NOUVEAU_FIELD_OPTION: SelectOption = { value: NOUVEAU_FIELD, label: '+ Créer un nouveau champ' }
const NOUVEAU_SOUS_BLOC_OPTION: SelectOption = { value: NOUVEAU_SOUS_BLOC, label: '+ Créer un nouveau sous-bloc' }
const AUCUN_SOUS_BLOC_OPTION: SelectOption = { value: AUCUN_SOUS_BLOC, label: 'Aucun' }

const TYPE_OPTIONS: SelectOption[] = TYPES_CHAMP.map(t => ({ value: t.code, label: t.libelle }))

interface Ligne {
    id: number
    fieldSelection: SelectOption | null
    nouveauFieldLabel: string
    nouveauFieldCode: string
    sousBlocSelection: SelectOption | null
    nouveauSousBlocLabel: string
    isCore: boolean
    defaultState: RequiredFieldStateCode
}

function ligneVide(id: number): Ligne {
    return {
        id,
        fieldSelection: null,
        nouveauFieldLabel: '',
        nouveauFieldCode: '',
        sousBlocSelection: AUCUN_SOUS_BLOC_OPTION,
        nouveauSousBlocLabel: '',
        isCore: false,
        defaultState: 'OPTIONAL',
    }
}

export default function AjoutChampForm({ fields, sousBlocsExistants, onCreateField, onCreateSubBloc, onSubmit, onCancel }: AjoutChampFormProps) {
    // Créés localement pendant cette session du formulaire (en plus de ce qui existait déjà en
    // base), pour qu'un sous-bloc tout juste créé sur une ligne soit immédiatement sélectionnable
    // sur les autres lignes, sans le recréer en double.
    const [sousBlocsLocaux, setSousBlocsLocaux] = useState<SousBlocOption[]>(sousBlocsExistants)

    const fieldOptions: SelectOption[] = [
        NOUVEAU_FIELD_OPTION,
        ...fields.map(f => ({ value: String(f.id), label: `${f.label} (${f.code})` })),
    ]
    const sousBlocOptions: SelectOption[] = [
        NOUVEAU_SOUS_BLOC_OPTION,
        AUCUN_SOUS_BLOC_OPTION,
        ...sousBlocsLocaux
            .slice()
            .sort((a, b) => a.label.localeCompare(b.label, 'fr'))
            .map(sb => ({ value: String(sb.id), label: sb.label })),
    ]

    const nextId = useRef(1)
    const [lignes, setLignes] = useState<Ligne[]>([ligneVide(0)])
    const [typesGlobaux, setTypesGlobaux] = useState<SelectOption[]>([])
    const totalAAjouter = lignes.length * Math.max(typesGlobaux.length, 1)
    const [creatingFieldId, setCreatingFieldId] = useState<number | null>(null)
    const [creatingSousBlocId, setCreatingSousBlocId] = useState<number | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const updateLigne = (id: number, patch: Partial<Ligne>) => {
        setLignes(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l))
    }

    const addLigne = () => {
        setLignes(prev => [...prev, ligneVide(nextId.current++)])
    }

    const removeLigne = (id: number) => {
        setLignes(prev => prev.filter(l => l.id !== id))
    }

    // Crée le champ immédiatement : il devient sélectionnable sur toutes les lignes (y compris
    // celles déjà ajoutées) dès que "fields" est mis à jour par le hook parent.
    const handleCreerField = async (ligne: Ligne) => {
        if (!ligne.nouveauFieldLabel || !ligne.nouveauFieldCode) {
            setError('Le libellé et le code du nouveau champ sont requis')
            return
        }
        setError(null)
        setCreatingFieldId(ligne.id)
        try {
            const field = await onCreateField(ligne.nouveauFieldLabel, ligne.nouveauFieldCode)
            updateLigne(ligne.id, {
                fieldSelection: { value: String(field.id), label: `${field.label} (${field.code})` },
                nouveauFieldLabel: '',
                nouveauFieldCode: '',
            })
        } catch (err: any) {
            setError(err.message || 'Erreur lors de la création du champ')
        } finally {
            setCreatingFieldId(null)
        }
    }

    // Idem pour le sous-bloc : ajouté à sousBlocsLocaux pour être immédiatement disponible sur
    // les autres lignes du formulaire.
    const handleCreerSousBloc = async (ligne: Ligne) => {
        if (!ligne.nouveauSousBlocLabel) {
            setError('Le libellé du nouveau sous-bloc est requis')
            return
        }
        setError(null)
        setCreatingSousBlocId(ligne.id)
        try {
            const sousBloc = await onCreateSubBloc(ligne.nouveauSousBlocLabel)
            const option = { id: sousBloc.id, label: ligne.nouveauSousBlocLabel }
            setSousBlocsLocaux(prev => [...prev, option])
            updateLigne(ligne.id, {
                sousBlocSelection: { value: String(option.id), label: option.label },
                nouveauSousBlocLabel: '',
            })
        } catch (err: any) {
            setError(err.message || 'Erreur lors de la création du sous-bloc')
        } finally {
            setCreatingSousBlocId(null)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)

        for (const ligne of lignes) {
            if (!ligne.fieldSelection || ligne.fieldSelection.value === NOUVEAU_FIELD) {
                setError('Validez (bouton "Créer") ou sélectionnez un champ sur chaque ligne')
                return
            }
            if (ligne.sousBlocSelection?.value === NOUVEAU_SOUS_BLOC) {
                setError('Validez (bouton "Créer") le nouveau sous-bloc avant d\'ajouter')
                return
            }
        }

        setSubmitting(true)
        try {
            // Les types sélectionnés en haut du formulaire s'appliquent à toutes les lignes :
            // chaque champ ajouté donne un template_field par type (ou un seul si aucun type).
            const payloads: TemplateFieldPayload[] = lignes.flatMap(ligne => {
                const base = {
                    fieldId: Number((ligne.fieldSelection as SelectOption).value),
                    subBlockId: ligne.sousBlocSelection && ligne.sousBlocSelection.value !== AUCUN_SOUS_BLOC
                        ? Number(ligne.sousBlocSelection.value)
                        : null,
                    isCore: ligne.isCore,
                    defaultState: ligne.defaultState,
                }

                return typesGlobaux.length > 0
                    ? typesGlobaux.map((t): TemplateFieldPayload => ({ ...base, type: t.value }))
                    : [{ ...base, type: null } as TemplateFieldPayload]
            })

            await onSubmit(payloads)
        } catch (err: any) {
            setError(err.message || 'Erreur lors de l\'ajout des champs')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <form className="border rounded p-3 mb-3 tw-bg-gray-50" onSubmit={handleSubmit}>
            {error && <div className="alert alert-danger py-1">{error}</div>}

            <div className="form-group" style={{ maxWidth: 400 }}>
                <label>Types (s'appliquent à tous les champs ajoutés ci-dessous)</label>
                <Select
                    multi
                    options={TYPE_OPTIONS}
                    value={typesGlobaux}
                    onChange={(options: SelectOption[]) => setTypesGlobaux(options)}
                    placeholder="Aucun type"
                    noResultsText="Aucun type trouvé"
                />
                {typesGlobaux.length > 1 && (
                    <small className="text-muted d-block mt-1">
                        Chaque champ sera dupliqué pour ces {typesGlobaux.length} types.
                    </small>
                )}
            </div>

            <hr />

            {lignes.map((ligne, index) => (
                <div className="form-row align-items-start border-bottom pb-2 mb-2" key={ligne.id}>
                    <div className="form-group col-md-5">
                        {index === 0 && <label>Champ</label>}
                        <Select
                            options={fieldOptions}
                            value={ligne.fieldSelection}
                            onChange={(option: SelectOption | null) => updateLigne(ligne.id, { fieldSelection: option })}
                            placeholder="Rechercher un champ..."
                            noResultsText="Aucun champ trouvé"
                        />
                        {ligne.fieldSelection?.value === NOUVEAU_FIELD && (
                            <div className="mt-2">
                                <input
                                    type="text"
                                    className="form-control form-control-sm mb-1"
                                    placeholder="Libellé"
                                    value={ligne.nouveauFieldLabel}
                                    onChange={e => updateLigne(ligne.id, { nouveauFieldLabel: e.target.value })}
                                />
                                <input
                                    type="text"
                                    className="form-control form-control-sm mb-1"
                                    placeholder="Code"
                                    value={ligne.nouveauFieldCode}
                                    onChange={e => updateLigne(ligne.id, { nouveauFieldCode: e.target.value })}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleCreerField(ligne)}
                                    disabled={creatingFieldId === ligne.id}
                                >
                                    {creatingFieldId === ligne.id ? 'Création...' : 'Créer ce champ'}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="form-group col-md-3">
                        {index === 0 && <label>Sous-bloc</label>}
                        <Select
                            options={sousBlocOptions}
                            value={ligne.sousBlocSelection}
                            onChange={(option: SelectOption | null) => updateLigne(ligne.id, { sousBlocSelection: option })}
                            placeholder="Rechercher un sous-bloc..."
                            noResultsText="Aucun sous-bloc trouvé"
                        />
                        {ligne.sousBlocSelection?.value === NOUVEAU_SOUS_BLOC && (
                            <div className="mt-2">
                                <input
                                    type="text"
                                    className="form-control form-control-sm mb-1"
                                    placeholder="Libellé du sous-bloc"
                                    value={ligne.nouveauSousBlocLabel}
                                    onChange={e => updateLigne(ligne.id, { nouveauSousBlocLabel: e.target.value })}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleCreerSousBloc(ligne)}
                                    disabled={creatingSousBlocId === ligne.id}
                                >
                                    {creatingSousBlocId === ligne.id ? 'Création...' : 'Créer ce sous-bloc'}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="form-group col-md-2">
                        {index === 0 && <label>État par défaut</label>}
                        <select
                            className="form-control form-control-sm"
                            value={ligne.defaultState}
                            onChange={e => updateLigne(ligne.id, { defaultState: e.target.value as RequiredFieldStateCode })}
                            disabled={ligne.isCore}
                        >
                            {REQUIRED_FIELD_STATES.map(etat => <option key={etat.code} value={etat.code}>{etat.label}</option>)}
                        </select>
                    </div>

                    <div className="form-group col-md-1 d-flex align-items-center">
                        <label className={(index === 0 ? "mt-4 " : "") + "mb-0"}>
                            <input
                                type="checkbox"
                                className="mr-1"
                                checked={ligne.isCore}
                                onChange={e => updateLigne(ligne.id, {
                                    isCore: e.target.checked,
                                    defaultState: e.target.checked ? 'REQUIRED' : ligne.defaultState,
                                })}
                            />
                            <small>Socle</small>
                        </label>
                    </div>

                    <div className="col-12 text-right">
                        {lignes.length > 1 && (
                            <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeLigne(ligne.id)}>
                                <i className="fa fa-trash mr-1" /><small>Retirer cette ligne</small>
                            </button>
                        )}
                    </div>
                </div>
            ))}

            <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={addLigne}>
                <i className="fa fa-plus mr-1" />Ajouter une autre ligne
            </button>

            <div>
                <button type="submit" className="btn btn-eseller-color btn-sm mr-2" disabled={submitting}>
                    {submitting ? 'Ajout...' : `Ajouter (${totalAAjouter})`}
                </button>
                <button type="button" className="btn btn-light btn-sm" onClick={onCancel} disabled={submitting}>
                    Annuler
                </button>
            </div>
        </form>
    )
}
